/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import { ContentBlock, Heading, Text, CardCarousel, Badge } from 'sarsaparilla';

import bikingImg from './illustrations/biking.webp';
import bikingImg2x from './illustrations/biking@2x.webp';
import educationImg from './illustrations/education.webp';
import educationImg2x from './illustrations/education@2x.webp';
import fishingImg from './illustrations/fishing.webp';
import fishingImg2x from './illustrations/fishing@2x.webp';
import hikingImg from './illustrations/hiking.webp';
import hikingImg2x from './illustrations/hiking@2x.webp';
import waterActivitiesImg from './illustrations/waterActivities.webp';
import waterActivitiesImg2x from './illustrations/waterActivities@2x.webp';
import wildlifeViewingImg from './illustrations/wildlifeViewing.webp';
import wildlifeViewingImg2x from './illustrations/wildlifeViewing@2x.webp';

import styles from './ActivitiesCarousel.module.scss';

export function ActivitiesCarousel() {
    return (
        <section>
            <ContentBlock>
                <Heading headingLevel={2} appearance="h4" hasUnderline="left">
                    <span>
                        Campground Explorer{' '}
                        <Badge appearance="info" className="ml-half">
                            Beta
                        </Badge>
                    </span>
                </Heading>
                <Text className="mb-2">
                    Choose an activity and discover campgrounds nearby!
                </Text>
            </ContentBlock>

            <CardCarousel
                accessibleName="Campground Explorer"
                gaTrackingId="230736205354"
                simulateContentBlock
                itemsPerBreakpoint={{ xs: 1, sm: 2, md: 4, xl: 5, xxl: 6 }}
                peekPerBreakpoint={{ xs: 104, sm: 24, md: 0 }}
                overscanPerBreakpoint={{ xs: 3, md: 8 }}
            >
                <ActivityCard
                    label="Biking"
                    tagName="biking"
                    image={{ src: bikingImg, srcSet: bikingImg2x }}
                    className={styles.biking}
                />

                <ActivityCard
                    label="Water Activities"
                    tagName="water_activities"
                    image={{
                        src: waterActivitiesImg,
                        srcSet: waterActivitiesImg2x,
                    }}
                    className={styles.waterActivities}
                />

                <ActivityCard
                    label="Hiking"
                    tagName="hiking"
                    image={{ src: hikingImg, srcSet: hikingImg2x }}
                    className={styles.hiking}
                />

                <ActivityCard
                    label="Fishing"
                    tagName="fishing"
                    image={{ src: fishingImg, srcSet: fishingImg2x }}
                    className={styles.fishing}
                />

                <ActivityCard
                    label="Wildlife Viewing"
                    tagName="wildlife_viewing"
                    image={{
                        src: wildlifeViewingImg,
                        srcSet: wildlifeViewingImg2x,
                    }}
                    className={styles.wildlifeViewing}
                />

                <ActivityCard
                    label="Education / Programs"
                    tagName="education"
                    image={{ src: educationImg, srcSet: educationImg2x }}
                    className={styles.education}
                />
            </CardCarousel>
        </section>
    );
}

interface ActivityCardProps {
    label: string;
    tagName: string;
    image: { src: string; srcSet: string };
    className: string;
}

function ActivityCard({ label, tagName, image, className }: ActivityCardProps) {
    return (
        <div className={cx(styles.card, className)}>
            <div className={styles.backdrop}>
                <a
                    href={`/search?inventory_type=camping&activity_tags=${tagName}`}
                    className={styles.cardLink}
                    aria-label={`Campgrounds with ${label}`}
                    data-ga-tracking-id="630734203378"
                    data-ga-tracking-value={label}
                >
                    {label}
                </a>
                <img
                    src={image.src}
                    srcSet={`${image.srcSet} 2x`}
                    alt=""
                    className={styles.cardImage}
                    loading="lazy"
                />
            </div>
        </div>
    );
}
